import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ClipboardList, Copy, Check, Send, Loader2, ExternalLink, Home, KeyRound, Camera, StickyNote, Sparkles, RefreshCw } from 'lucide-react';
import { PROPERTY_TYPES, PETS, PARKING, GOALS, AREAS, SOMEONE_HOME, ACCESS, label } from '../intake/intakeOptions';
import { SERVICE_CARDS, PATHWAYS, OPTION_LABEL, visibleQuestions, SOMEONE_HOME as V2_HOME, ACCESS as V2_ACCESS } from '../intake/v2/pathways';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  sent:        { label: 'Sent',        cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  opened:      { label: 'Opened',      cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  completed:   { label: 'Completed',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  revoked:     { label: 'Revoked',     cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};
const when = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null);
const sqftShort = (v) => ({ under_1000: '<1,000 sq ft', '1000_1500': '1,000–1,500 sq ft', '1500_2000': '1,500–2,000 sq ft', '2000_2500': '2,000–2,500 sq ft', '2500_3500': '2,500–3,500 sq ft', over_3500: '3,500+ sq ft' }[v] || null);

export const AuthImage = ({ src, headers, className, alt = '' }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    axios.get(src, { headers, responseType: 'blob' }).then(r => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [src, headers]);
  return url ? <img src={url} alt={alt} className={className} /> : <div className={`${className} bg-zinc-100 animate-pulse`} />;
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: 'Not Requested', cls: 'bg-zinc-50 text-zinc-500 border-zinc-200' };
  return <Badge variant="outline" className={`${s.cls} text-[10px]`} data-testid="intake-status-badge">{s.label}</Badge>;
};

const chipLabel = (a) => AREAS.find(o => o.value === a)?.label || a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">{items.map(a => <span key={a} className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-800 border border-orange-200 text-[11px] font-medium">{chipLabel(a)}</span>)}</div>
);

const Block = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />}{title}</p>
    {children}
  </div>
);

const propertyLine = (d) => [
  d.bedrooms != null ? `${d.bedrooms} Bed` : null,
  d.bathrooms != null ? `${d.bathrooms} Bath` : null,
  sqftShort(d.square_footage),
].filter(Boolean).join(' • ');

const V2_LABEL = (q, v) => Array.isArray(v) ? v.map(x => OPTION_LABEL(q.options, x)) : (q.options ? OPTION_LABEL(q.options, v) : String(v));
const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);

/* Renders EVERY answered question of the pathway, grouped by its stage (source of truth: pathways.js). */
export const IntakeSummaryV2 = ({ intake, headers, compact = true }) => {
  const d = intake.data || {};
  const photos = intake.photos || [];
  const path = PATHWAYS[d.service];
  if (!path) return null;
  const qs = visibleQuestions(d.service, d).filter(q => !['review', 'photos'].includes(q.type));
  const groups = path.stages.map((name, si) => {
    const rows = [];
    qs.filter(q => q.stage === si).forEach(q => {
      if (q.type === 'contact') {
        [['Name', 'contact_name'], ['Phone', 'contact_phone'], ['Email', 'contact_email']].forEach(([k, f]) => { if (d[f]) rows.push({ k, v: d[f] }); });
        Object.entries(intake.contact_corrections || {}).forEach(([f, c]) => rows.push({ k: `Corrected ${f.replace('_', ' ')}`, v: `${c.from || '—'} → ${c.to}`, sensitive: true }));
        return;
      }
      if (q.type === 'access') {
        if (d.someone_home) rows.push({ k: 'Someone present', v: OPTION_LABEL(V2_HOME, d.someone_home) });
        if (d.access_method) rows.push({ k: 'Entry method', v: OPTION_LABEL(V2_ACCESS, d.access_method) });
        if (d.entry_instructions) rows.push({ k: 'Entry instructions', v: d.entry_instructions, sensitive: true });
        return;
      }
      if (isEmpty(d[q.key])) return;
      rows.push({ k: q.title.replace(/\?$/, ''), v: V2_LABEL(q, d[q.key]), sensitive: ['entry_instructions', 'alarm_instructions', 'secure_areas_instructions'].includes(q.key) });
    });
    if (si === 0 && d.address?.line1) rows.unshift({ k: 'Address', v: `${d.address.line1}${d.address.line2 ? `, ${d.address.line2}` : ''}, ${d.address.city || ''} ${d.address.state || ''} ${d.address.zip || ''}` });
    return { name, rows };
  }).filter(g => g.rows.length);
  const shown = compact ? groups.slice(0, 2) : groups;
  return (
    <div className="space-y-4 text-sm" data-testid="intake-summary-v2">
      <Block title="Service" testId="intake-summary-service"><p className="font-semibold text-zinc-900">{SERVICE_CARDS[d.service]?.label}</p></Block>
      {intake.initial_request && (intake.initial_request.message || intake.initial_request.lead_source) && (
        <Block title="From the first request" testId="intake-initial-request">
          {intake.initial_request.lead_source && <p className="text-xs text-zinc-600">Heard about us: <span className="font-medium text-zinc-900">{intake.initial_request.lead_source}</span></p>}
          {intake.initial_request.message && <p className="text-xs text-zinc-900 whitespace-pre-wrap mt-1">{intake.initial_request.message}</p>}
        </Block>
      )}
      {d.service === 'move' && <p className="text-[11px] text-zinc-500" data-testid="intake-move-note">One-time request. Detailing items are requests to review and confirm in scope; the preferred date is not booked.</p>}
      {shown.map(g => (
        <Block key={g.name} title={g.name} testId={`intake-group-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <div className="space-y-1.5">
            {g.rows.map(r => (
              <div key={r.k} className="flex flex-col sm:flex-row sm:justify-between sm:gap-4">
                <span className="text-xs text-zinc-500 sm:w-2/5 flex-shrink-0">{r.k}</span>
                {Array.isArray(r.v) ? <span className="flex flex-wrap gap-1">{r.v.map(x => <span key={x} className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-800 border border-orange-200 text-[11px] font-medium">{x}</span>)}</span>
                  : <span className={`text-xs sm:text-right whitespace-pre-wrap ${r.sensitive ? 'rounded-md bg-amber-50 border border-amber-100 px-2 py-1 text-zinc-800' : 'text-zinc-900 font-medium'}`}>{r.v}</span>}
              </div>
            ))}
          </div>
        </Block>
      ))}
      {compact && groups.length > 2 && <p className="text-[11px] text-zinc-400">+ {groups.length - 2} more sections in the full intake</p>}
      <Block icon={Camera} title={`Photos (${photos.length})`} testId="intake-summary-photos">
        {photos.length ? (
          <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-3 sm:grid-cols-4'} gap-2`}>
            {(compact ? photos.slice(0, 4) : photos).map(p => <AuthImage key={p.id} src={`${API_URL}/api/admin/intake/${intake.id}/photos/${p.id}`} headers={headers} alt="Client space photo" className="aspect-square w-full rounded-lg object-cover border border-zinc-200" />)}
          </div>
        ) : <p className="text-xs text-zinc-400">No photos</p>}
      </Block>
    </div>
  );
};

export const IntakeSummary = ({ intake, headers, compact = true }) => {
  if (intake.data?.service && PATHWAYS[intake.data.service]) return <IntakeSummaryV2 intake={intake} headers={headers} compact={compact} />;
  const d = intake.data || {};
  const photos = intake.photos || [];
  const addr = d.address?.line1 ? `${d.address.line1}${d.address.line2 ? `, ${d.address.line2}` : ''}, ${d.address.city || ''} ${d.address.state || ''} ${d.address.zip || ''}` : null;
  const v2q = d.service ? (PATHWAYS[d.service]?.questions || []) : [];
  const v2label = (key) => { const q = v2q.find(x => x.key === key); const v = d[key]; if (v == null || v === '' || (Array.isArray(v) && !v.length)) return null; return q?.options ? (Array.isArray(v) ? v.map(x => OPTION_LABEL(q.options, x)).join(', ') : OPTION_LABEL(q.options, v)) : String(v); };
  const V2_ROWS = [['job_type', 'Visit type'], ['needs_type', 'Needs'], ['frequency', 'Frequency'], ['preferred_date', 'Preferred date'], ['preferred_time', 'Preferred time'], ['last_cleaned', 'Last professional clean'], ['condition', 'Condition'], ['neglected_areas', 'Long-untouched areas'], ['neglected_description', 'Details'], ['addons', 'Add-ons'], ['guest_capacity', 'Sleeps'], ['turnover_frequency', 'Turnover frequency'], ['same_day_turnover', 'Same-day turnovers'], ['checkout_time', 'Checkout'], ['checkin_time', 'Check-in'], ['laundry_handling', 'Laundry'], ['supplies_provided', 'Supplies'], ['restock_needed', 'Restock essentials'], ['facility_type', 'Facility'], ['facility_sqft', 'Facility size'], ['restroom_count', 'Restrooms'], ['scope_areas', 'Scope'], ['schedule_frequency', 'Schedule'], ['schedule_window', 'Cleaning window'], ['walkthrough_needed', 'Walkthrough'], ['walkthrough_notes', 'Walkthrough notes']].map(([k, l]) => [l, v2label(k)]).filter(([, v]) => v);
  return (
    <div className="space-y-4 text-sm">
      {d.service && (
        <Block title="Service & Visit" testId="intake-summary-service">
          <p className="font-semibold text-zinc-900">{SERVICE_CARDS[d.service]?.label}</p>
          <div className="mt-1 space-y-0.5">{V2_ROWS.map(([l, v]) => <p key={l} className="text-xs text-zinc-600"><span className="font-semibold text-zinc-700">{l}:</span> {v}</p>)}</div>
        </Block>
      )}
      <Block icon={Home} title="Property" testId="intake-summary-property">
        <p className="font-semibold text-zinc-900">{propertyLine(d) || '—'}</p>
        <p className="text-xs text-zinc-600">{[label(PROPERTY_TYPES, d.property_type), d.occupancy ? (d.occupancy === 'occupied' ? 'Occupied' : 'Vacant') : null, d.floors ? `${d.floors} floor${d.floors > 1 ? 's' : ''}` : null].filter(Boolean).join(' • ')}</p>
        {!compact && addr && <p className="text-xs text-zinc-600 mt-1">{addr}</p>}
        {!compact && d.parking && <p className="text-xs text-zinc-600 mt-1">Parking: {label(PARKING, d.parking)}{d.parking_notes ? ` — ${d.parking_notes}` : ''}</p>}
        {d.pets && d.pets !== 'none' && <p className="text-xs text-zinc-600 mt-1">Pets: {label(PETS, d.pets)}</p>}
      </Block>
      <Block icon={Sparkles} title="Priorities" testId="intake-summary-priorities">
        {(d.focus_areas?.length || d.priority_areas?.length) ? <Chips items={d.focus_areas?.length ? d.focus_areas : d.priority_areas} /> : <p className="text-xs text-zinc-400">None selected</p>}
        {(d.focus_notes || d.concerns) && <p className="text-xs text-zinc-700 mt-2 whitespace-pre-wrap">{d.focus_notes || d.concerns}</p>}
      </Block>
      {!d.service && <Block title="Service Interest" testId="intake-summary-goal"><p className="font-semibold text-zinc-900">{label(GOALS, d.primary_goal)}</p></Block>}
      <Block icon={Camera} title={`Photos (${photos.length})`} testId="intake-summary-photos">
        {photos.length ? (
          <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-3 sm:grid-cols-4'} gap-2`}>
            {(compact ? photos.slice(0, 4) : photos).map(p => (
              <AuthImage key={p.id} src={`${API_URL}/api/admin/intake/${intake.id}/photos/${p.id}`} headers={headers} alt="Client space photo" className="aspect-square w-full rounded-lg object-cover border border-zinc-200" />
            ))}
          </div>
        ) : <p className="text-xs text-zinc-400">No photos</p>}
      </Block>
      <Block icon={KeyRound} title="Access" testId="intake-summary-access">
        <p className="text-zinc-900">{[d.someone_home ? label(SOMEONE_HOME, d.someone_home) : null, d.access_method ? `Entry: ${label(ACCESS, d.access_method)}` : null].filter(Boolean).join(' • ') || '—'}</p>
        {d.entry_instructions && <p className="text-xs text-zinc-700 mt-1 whitespace-pre-wrap rounded-lg bg-amber-50 border border-amber-100 p-2">{d.entry_instructions}</p>}
      </Block>
      {(d.pet_instructions || d.no_enter_areas || d.special_surfaces) && (
        <Block icon={StickyNote} title="Special Notes" testId="intake-summary-notes">
          <div className="space-y-1 text-xs text-zinc-700">
            {d.pet_instructions && <p><span className="font-semibold">Pets:</span> {d.pet_instructions}</p>}
            {d.no_enter_areas && <p><span className="font-semibold">Do not enter:</span> {d.no_enter_areas}</p>}
            {d.special_surfaces && <p><span className="font-semibold">Surfaces:</span> {d.special_surfaces}</p>}
          </div>
        </Block>
      )}
    </div>
  );
};

export const SendIntakeDialog = ({ open, onOpenChange, result, intake, headers, onSent }) => {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  if (!result) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.intake_url); } catch { /* fallthrough */ }
    setCopied(true);
    axios.post(`${API_URL}/api/admin/intake/${intake.id}/mark-sent`, { via: 'copy' }, { headers }).then(onSent).catch(() => {});
    toast.success('Intake link copied');
  };
  const sendSms = async () => {
    setSending(true);
    try {
      const c = await axios.post(`${API_URL}/api/admin/intake/${intake.id}/conversation`, {}, { headers });
      await axios.post(`${API_URL}/api/admin/conversations/${c.data.conversation_id}/messages`, { body: result.sms_body }, { headers: { ...headers, 'Idempotency-Key': `intake-${intake.id}-${result.intake_url.slice(-8)}` } });
      await axios.post(`${API_URL}/api/admin/intake/${intake.id}/mark-sent`, { via: 'sms' }, { headers });
      toast.success('Intake link sent by text');
      onSent?.(); onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the text message');
    } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-intake-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#F56F05]" /> Send Space Intake</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600">{result.existing ? 'A fresh secure link was generated for the existing request.' : 'A secure, private intake link was created.'} It expires in 30 days.</p>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono break-all text-zinc-700" data-testid="intake-link-text">{result.intake_url}</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Button variant="outline" onClick={copy} data-testid="copy-intake-link-btn">{copied ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Copy className="w-4 h-4 mr-2" />}{copied ? 'Copied' : 'Copy link'}</Button>
          <Button onClick={sendSms} disabled={sending} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="send-intake-sms-btn">{sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send via text</Button>
        </div>
        <p className="text-[11px] text-zinc-500">Text preview: “{result.sms_body}”</p>
      </DialogContent>
    </Dialog>
  );
};

export const FullIntakeDialog = ({ intake, open, onOpenChange, headers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="full-intake-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#F56F05]" /> Space Intake {intake && <StatusBadge status={intake.status} />}</DialogTitle>
        {intake?.submitted_at && <p className="text-xs text-zinc-500">Submitted {when(intake.submitted_at)}</p>}
      </DialogHeader>
      {intake && <IntakeSummary intake={intake} headers={headers} compact={false} />}
    </DialogContent>
  </Dialog>
);

export default function SpaceIntakeSection({ leadId, customerUserId, headers, autoOpenId, wrapper: Wrapper }) {
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [fullOpen, setFullOpen] = useState(false);
  const params = useMemo(() => ({ ...(leadId ? { lead_id: leadId } : {}), ...(customerUserId ? { customer_user_id: customerUserId } : {}) }), [leadId, customerUserId]);

  const load = useCallback(async () => {
    if (!leadId && !customerUserId) return;
    try { const r = await axios.get(`${API_URL}/api/admin/intake/by-entity`, { headers, params }); setIntakes(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, [headers, params, leadId, customerUserId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (autoOpenId && intakes.some(i => i.id === autoOpenId)) setFullOpen(true); }, [autoOpenId, intakes]);

  const latest = intakes[0];
  const create = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/intake/requests`, { lead_id: leadId || null, customer_user_id: customerUserId || null, origin_url: window.location.origin }, { headers });
      setSendResult(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create intake link'); }
    finally { setCreating(false); }
  };

  const action = (
    <Button size="sm" variant="outline" onClick={create} disabled={creating} className="border-[#F56F05]/30 text-[#F56F05] hover:bg-orange-50 hover:text-[#F56F05]" data-testid="send-space-intake-btn">
      {creating ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : latest && latest.status !== 'completed' ? <RefreshCw className="w-3 h-3 mr-1.5" /> : <Send className="w-3 h-3 mr-1.5" />}
      {latest && latest.status !== 'completed' ? 'Resend link' : 'Send Space Intake'}
    </Button>
  );

  const body = loading ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" /> : (
    <div className="space-y-3" data-testid="space-intake-section">
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={latest?.status} />
        {latest && <span className="text-[11px] text-zinc-500">
          {latest.status === 'completed' ? `Completed ${when(latest.submitted_at)}` : latest.opened_at ? `Opened ${when(latest.opened_at)}` : latest.sent_at ? `Sent ${when(latest.sent_at)}${latest.sent_via === 'sms' ? ' by text' : ''}` : `Link created ${when(latest.created_at)}`}
        </span>}
      </div>
      {!latest && <p className="text-xs text-zinc-500 italic">No intake requested yet. Send a secure “Tell Us About Your Space” link to collect property, priority, access and photo details.</p>}
      {latest?.status === 'completed' && (
        <>
          <IntakeSummary intake={latest} headers={headers} />
          <Button size="sm" variant="outline" className="w-full" onClick={() => setFullOpen(true)} data-testid="view-full-intake-btn"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Full Intake</Button>
        </>
      )}
      {Wrapper === undefined && <div className="pt-1">{action}</div>}
      <SendIntakeDialog open={!!sendResult} onOpenChange={(o) => !o && setSendResult(null)} result={sendResult} intake={sendResult} headers={headers} onSent={load} />
      <FullIntakeDialog intake={latest} open={fullOpen} onOpenChange={setFullOpen} headers={headers} />
    </div>
  );

  return Wrapper ? <Wrapper action={action}>{body}</Wrapper> : body;
}
